import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/ui/badge";
import type { TranslationKey } from "@/constants/i18n";
import { useLocaleStore, useTranslation } from "@/store/locale";

/** What the profile page and the employee view both know about an account. */
export interface AccountLike {
  user_name: string;
  full_name: string;
  avatar?: string | null;
  email_address: string | null;
  /** The three the Edit Profile sheet writes. Optional, because the employee
   *  view reads accounts from a list that does not carry them. */
  notes?: string | null;
  phone?: string | null;
  contacts?: { phone: string; country: string | null }[];
  status: "active" | "password_expired" | "account_locked" | "inactive" | "deleted";
  is_superuser: boolean;
  is_password_request: boolean;
  request_reason?: string | null;
  password_request_at?: string | null;
  force_password_change?: boolean;
  last_password_change_at?: string | null;
  last_login_at?: string | null;
  club: { club_name: string; club_code?: string } | null;
  is_temporary_club: boolean;
  temporary_club: { club_name: string; club_code?: string } | null;
  temporary_club_reason: string | null;
  temporary_start_date: string | null;
  temporary_end_date: string | null;
  has_active_temporary_club: boolean;
  groups: { name: string }[];
  permissions?: string[];
  created_at: string;
  updated_at: string;
}

export const accountName = (account: AccountLike) => account.full_name || account.user_name;

export function useAccountRoles(account: AccountLike): string {
  const t = useTranslation();
  return account.is_superuser
    ? t("shell.roleSuperuser")
    : account.groups.map((group) => group.name).join(", ") || t("shell.roleMember");
}

/** Picture, name, employee number and the three pills; slots for the picture control and actions. */
export function AccountBand({
  account,
  avatar,
  actions,
}: {
  account: AccountLike;
  avatar?: ReactNode;
  actions?: ReactNode;
}) {
  const t = useTranslation();
  const roles = useAccountRoles(account);
  const name = accountName(account);
  return (
    <div className="flex flex-wrap items-center gap-5 p-5 sm:p-6">
      {avatar ?? (
        <Avatar
          src={account.avatar ?? null}
          name={name}
          className="size-20 rounded-[18px] text-2xl"
        />
      )}
      <div className="min-w-0 flex-1">
        <h2 className="text-title-2 text-heading truncate">{name}</h2>
        <p className="text-fg-muted text-callout mt-0.5 truncate">{account.user_name}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusPill status={account.status} />
          <Badge tone="neutral">{roles}</Badge>
          {account.has_active_temporary_club && account.temporary_club ? (
            <Badge tone="elevated">
              {t("profile.postingClub")} {account.temporary_club.club_name}
            </Badge>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">{actions}</div>
      ) : null}
    </div>
  );
}

/**
 * The two groups of facts: the account, and its security. `extra` rows join
 * the account group.
 *
 * Temporary posting was a third, and is gone from here. It is HR's record of
 * where somebody is working this month, not something the person reads about
 * themselves on their own profile — and an empty "No temporary posting on
 * record" was what it said for nearly everybody, every time. The band above
 * still shows an active posting as a pill, which is the part that is worth
 * knowing at a glance.
 */
export function AccountSections({
  account,
  extra = [],
}: {
  account: AccountLike;
  extra?: { label: string; value: ReactNode; muted?: boolean }[];
}) {
  const t = useTranslation();
  const locale = useLocaleStore((state) => state.locale);
  const roles = useAccountRoles(account);
  const when = (value: string | null | undefined) =>
    value
      ? new Date(value).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
      : t("profile.never");
  const day = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(locale, { dateStyle: "medium" }) : "—";
  const clubLabel = (club: AccountLike["club"]) =>
    club ? (club.club_code ? `${club.club_name} (${club.club_code})` : club.club_name) : null;

  return (
    <>
      <Section title={t("profile.sectionAccount")}>
        <Row label={t("profile.fullName")} value={account.full_name} />
        <Row label={t("profile.employeeId")} value={account.user_name} />
        <Row label={t("profile.email")} value={account.email_address} />
        {account.phone ? <Row label={t("profile.phone")} value={account.phone} /> : null}
        {account.contacts?.length ? (
          <Row
            label={t("profile.contacts")}
            value={account.contacts.map((contact) => contact.phone).join(", ")}
          />
        ) : null}
        <Row
          label={t("profile.club")}
          value={clubLabel(account.club) ?? t("profile.noClub")}
          muted={!account.club}
        />
        <Row
          label={t("profile.roles")}
          value={account.is_superuser ? t("profile.superuser") : roles}
        />
        {/* Not for a superuser: the row above already says so, and "134
            granted" under it invites the question of which 134 and why not
            135. They hold everything there is, including anything added
            tomorrow, and a count of today's catalogue would be a smaller
            number than the truth. */}
        {account.permissions && !account.is_superuser ? (
          <Row
            label={t("profile.permissions")}
            value={<PermissionList codenames={account.permissions} />}
          />
        ) : null}
        {extra.map((row) => (
          <Row key={row.label} label={row.label} value={row.value} muted={row.muted ?? false} />
        ))}
        {/* Last in the group: it is the one row whose length is the person's
            own, and a paragraph between two one-line facts breaks the column. */}
        {account.notes ? <Row label={t("profile.notes")} value={account.notes} /> : null}
      </Section>

      <Section title={t("profile.sectionSecurity")}>
        <div className="grid lg:grid-cols-2 lg:gap-x-8">
          <Row
            label={t("profile.status")}
            value={t(`status.${account.status}` as TranslationKey)}
          />
          <Row label={t("profile.lastLogin")} value={when(account.last_login_at)} />
          <Row
            label={t("profile.passwordChanged")}
            value={when(account.last_password_change_at)}
          />
          <Row
            label={t("profile.forceChange")}
            value={
              account.force_password_change
                ? t("profile.forceChangeYes")
                : t("profile.forceChangeNo")
            }
          />
          <Row
            label={t("profile.openRequest")}
            value={
              account.is_password_request
                ? `${account.request_reason ?? ""} · ${when(account.password_request_at)}`
                : t("profile.openRequestNone")
            }
            muted={!account.is_password_request}
          />
          <Row label={t("profile.created")} value={when(account.created_at)} />
          <Row label={t("profile.updated_at")} value={when(account.updated_at)} />
        </div>
      </Section>
    </>
  );
}

/** One group of facts: a titled band, then the rows. */
function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section className="border-line border-t">
      <div className="bg-surface-inset/60 flex items-center justify-between gap-3 px-5 py-2.5 sm:px-6">
        <h2 className="text-fg text-subhead font-semibold tracking-wide">{title}</h2>
        {badge ? (
          <span className="bg-accent-soft text-accent text-footnote rounded-full px-2.5 py-0.5 font-medium">
            {badge}
          </span>
        ) : null}
      </div>
      <dl className="px-1 py-2 sm:px-2">{children}</dl>
    </section>
  );
}

/**
 * The verbs a codename can start with, in the order the Roles grid shows them.
 *
 * Same words as that grid, so the two screens describe one grant the same way:
 * somebody reading "Edit — member basic information" here and looking for it
 * there should not have to work out that the codename says `change`.
 *
 * Anything unlisted keeps its own word rather than being dropped — a codename
 * this does not know about is still one the account holds.
 */
const ACTIONS: { action: string; label: TranslationKey }[] = [
  { action: "view", label: "roles.view" },
  { action: "add", label: "roles.add" },
  { action: "change", label: "roles.change" },
  { action: "delete", label: "roles.remove" },
  { action: "view_own", label: "roles.own" },
  { action: "hide", label: "roles.hide" },
];

/**
 * What this account may do: the count, and the list behind it.
 *
 * A number on its own answers nothing anybody asks. "Why can I not open
 * Configuration" is the question, and the count is the one fact on the page
 * that could answer it while being the one fact that does not. Folded away by
 * default, because for most people most days it is a number they glance past.
 *
 * Grouped by the verb. A hundred and thirty codenames in one column is a list
 * nobody reads to the end; under half a dozen headings it is six short lists,
 * and "what may I edit?" is a question the shape of the page answers.
 *
 * Read from the codenames the profile already carries — a friendlier name
 * would mean `GET /permissions`, which needs `view__permission`, which is
 * exactly the kind of grant somebody looking at this page does not have. So
 * `change__member_basic_info` is shown as its two halves, which is most of the
 * way to English and asks nothing of the server.
 */
function PermissionList({ codenames }: { codenames: string[] }) {
  const t = useTranslation();
  const held = codenames.map((codename) => {
    const [action = codename, ...rest] = codename.split("__");
    return { codename, action, subject: rest.join("__").replace(/_/g, " ") };
  });

  const known = new Set(ACTIONS.map((entry) => entry.action));
  const groups = [
    ...ACTIONS.map(({ action, label }) => ({ key: action, label: t(label), action })),
    // Whatever the list above has no word for, under its own word.
    ...[...new Set(held.map((row) => row.action))]
      .filter((action) => !known.has(action))
      .sort()
      .map((action) => ({ key: action, label: sentenceCase(action), action })),
  ]
    .map((group) => ({
      ...group,
      subjects: held
        .filter((row) => row.action === group.action)
        .map((row) => row.subject)
        .sort((a, b) => a.localeCompare(b)),
    }))
    .filter((group) => group.subjects.length > 0);

  return (
    <details className="group/perms">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-1.5 rounded marker:content-none">
        {t("profile.permissionsCount").replace("{count}", String(codenames.length))}
        <ChevronDown
          className="text-fg-subtle size-4 transition-transform group-open/perms:rotate-180"
          aria-hidden
        />
        <span className="sr-only">{t("profile.permissionsShow")}</span>
      </summary>
      {/* Its own scroller: a hundred and thirty rows inline turns the page
          into a list of codenames with an account somewhere above it. */}
      <div className="border-line mt-2 grid max-h-72 gap-4 overflow-y-auto rounded-lg border p-3">
        {groups.map((group) => (
          <section key={group.key}>
            <h4 className="text-fg text-footnote font-semibold">
              {group.label}
              <span className="text-fg-subtle ml-1.5 font-normal tabular-nums">
                {group.subjects.length}
              </span>
            </h4>
            <ul className="mt-1 grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
              {group.subjects.map((subject) => (
                <li key={subject} className="text-footnote text-fg-muted truncate">
                  {subject}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </details>
  );
}

/** `view_own` → `View own`; the codename's own word, not a translation of it. */
const sentenceCase = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1).replace(/_/g, " ");

function Row({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="grid gap-0.5 px-4 py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-fg-muted text-callout">{label}</dt>
      <dd className={`text-callout min-w-0 break-words ${muted ? "text-fg-muted" : "text-fg"}`}>
        {value ?? <span className="text-fg-muted">—</span>}
      </dd>
    </div>
  );
}

/**
 * The account's status, as a badge with a dot as well as a colour.
 *
 * Only `active` is green. Everything else is a reason someone cannot sign in,
 * and they are not interchangeable — `password_expired` clears itself with a
 * reset, `inactive` needs an administrator — so the word carries the meaning
 * and the colour only draws the eye.
 */
export function StatusPill({ status }: { status: AccountLike["status"] }) {
  const t = useTranslation();
  const tone =
    status === "active"
      ? "success"
      : status === "password_expired"
        ? "warning"
        : status === "deleted"
          ? "neutral"
          : "danger";
  return <Badge tone={tone}>{t(`status.${status}` as TranslationKey)}</Badge>;
}
